import * as React from "react";
import { isSameMonth } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, useDayPicker, useNavigation, type CaptionProps } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// Fluid shadcn wrapper for react-day-picker v8. The stock wrapper pinned every
// cell to h-9 w-9 (36px) inside flex rows, so a month was always a fixed 252px
// block, on phones it sat left-aligned with dead space beside it and tap
// targets were below the 44px guideline. Rows are now 7-column grids and cells
// size to their container (square, ≥40px), so one month fills the screen width
// on mobile and two months share the space on desktop. Keep gap-0: the range
// bar relies on adjacent cells touching.
// Month header with the arrows where the eye expects them. react-day-picker
// puts "previous" on the first month and "next" on the LAST month, which is
// right for months side by side but wrong once they stack on a phone: the
// forward arrow ends up at the bottom of the sheet. Stacking is decided by the
// `months` class (flex-col below sm), so the same breakpoint decides here:
// below sm both arrows sit on the first header, from sm up the layout is the
// library's.
function StackAwareCaption({ displayMonth, id }: CaptionProps) {
  const {
    classNames,
    numberOfMonths,
    locale,
    disableNavigation,
    formatters: { formatCaption },
    labels: { labelPrevious, labelNext },
  } = useDayPicker();
  const { previousMonth, nextMonth, goToMonth, displayMonths } = useNavigation();

  const index = displayMonths.findIndex((m) => isSameMonth(m, displayMonth));
  const isFirst = index <= 0;
  const isLast = index === displayMonths.length - 1;
  const multi = numberOfMonths > 1;
  const showPrevious = !disableNavigation && isFirst;
  const showNext = !disableNavigation && (isFirst || isLast);
  const nextVisibility = !multi ? "" : isFirst ? "sm:hidden" : "hidden sm:inline-flex";

  return (
    <div className={classNames.caption}>
      <div className={classNames.caption_label} aria-live="polite" role="presentation" id={id}>
        {formatCaption(displayMonth, { locale })}
      </div>
      <div className={classNames.nav}>
        {showPrevious && (
          <button
            type="button"
            name="previous-month"
            aria-label={labelPrevious(previousMonth, { locale })}
            className={cn(classNames.nav_button, classNames.nav_button_previous)}
            disabled={!previousMonth}
            onClick={() => previousMonth && goToMonth(previousMonth)}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}
        {showNext && (
          <button
            type="button"
            name="next-month"
            aria-label={labelNext(nextMonth, { locale })}
            className={cn(classNames.nav_button, classNames.nav_button_next, nextVisibility)}
            disabled={!nextMonth}
            onClick={() => nextMonth && goToMonth(nextMonth)}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function Calendar({ className, classNames, components, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-6 sm:space-x-6 sm:space-y-0",
        month: "space-y-3 w-full sm:min-w-[18rem]",
        caption: "flex justify-center pt-1 relative items-center h-9",
        caption_label: "text-base font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-9 w-9 min-h-0 bg-transparent p-0 opacity-70 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-0",
        nav_button_next: "absolute right-0",
        table: "w-full border-collapse",
        head_row: "grid grid-cols-7",
        head_cell: "text-muted-foreground w-full text-center font-normal text-[0.8rem] py-1",
        row: "grid grid-cols-7 w-full mt-0.5",
        cell: "w-full aspect-square h-auto min-h-[40px] text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-range-start)]:rounded-l-md [&:has([aria-selected].day-outside)]:bg-muted/50 [&:has([aria-selected])]:bg-muted first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-full w-full min-h-0 p-0 font-normal rounded-md aria-selected:opacity-100",
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-muted text-primary font-semibold",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-muted/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-40",
        day_range_middle: "aria-selected:bg-muted aria-selected:text-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{ Caption: StackAwareCaption, ...components }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
